/**
 * One Rule Element instance on an Entry (Foundry: rule-element/base.ts): validated source,
 * predicate, priority, and a record of anything that went wrong. Handlers are looked up by key;
 * an instance without a handler is Unsupported.
 */
import type { DamageDice } from "../damage-dice";
import type { EngineState } from "../state";
import { EngineItem } from "../items";
import type { Modifier } from "../modifiers";
import type { EngineWeapon } from "../weapons";
import type { RawPredicate } from "../predicate";
import { isValidPredicate, testPredicate } from "../predicate";
import { sluggify } from "../slug";

export type RuleSource = Record<string, unknown> & { key: string };

export interface RuleHandler {
  key: string;
  /** Default priority when the source gives none. */
  priority?: (source: RuleSource) => number;
  /** Validate and normalise the source. Call `rule.fail` for malformed data. */
  parse: (rule: RuleInstance, state: EngineState) => void;
  /** Run once every Entry's structural data has been applied (Foundry: onApplyActiveEffects). */
  onApplyActiveEffects?: (rule: RuleInstance, state: EngineState) => void;
  /** Run before statistics are derived (Foundry: beforePrepareData). */
  beforePrepareData?: (rule: RuleInstance, state: EngineState) => void;
  /** Run after every statistic has been derived (Foundry: afterPrepareData). */
  afterPrepareData?: (rule: RuleInstance, state: EngineState) => void;
  /** Disable damage parts a strike's damage roll may not use (Foundry: applyDamageExclusion, battle forms). */
  applyDamageExclusion?: (rule: RuleInstance, state: EngineState, weapon: EngineWeapon, parts: (Modifier | DamageDice)[]) => void;
}

export class RuleInstance {
  readonly key: string;
  readonly index: number;
  readonly item: EngineItem;
  readonly source: RuleSource;
  readonly handler: RuleHandler | null;
  label: string;
  slug: string | null;
  priority: number;
  predicate: RawPredicate;
  ignored: boolean;
  /** Messages explaining why the Rule Element was ignored or partially applied. */
  readonly problems: string[] = [];
  /** Handler-specific parsed data. */
  data: Record<string, unknown> = {};

  constructor(item: EngineItem, index: number, source: RuleSource, handler: RuleHandler | null) {
    this.item = item;
    this.index = index;
    this.source = source;
    this.key = source.key;
    this.handler = handler;
    this.slug = typeof source.slug === "string" && source.slug ? sluggify(source.slug) : null;
    const priority = source.priority;
    this.priority = typeof priority === "number" && Number.isInteger(priority) ? priority : (handler?.priority?.(source) ?? 100);
    this.ignored = source.ignored === true;
    this.predicate = [];
    if (source.predicate !== undefined) {
      if (isValidPredicate(source.predicate)) this.predicate = structuredClone(source.predicate);
      else this.fail("predicate: malformed");
    }
    this.label = typeof source.label === "string" && source.label ? source.label : item.name;
    // Spinoff composition rules are always inactive.
    if (typeof source.spinoff === "string" && source.spinoff) this.ignored = true;
  }

  /** Record a validation failure and stop applying this Rule Element (Foundry: failValidation). */
  fail(message: string): void {
    this.problems.push(message);
    this.ignored = true;
  }

  /** The label without a leading "Effect:"-style title, for modifier labels. */
  get reducedLabel(): string {
    return this.label === this.item.name ? reduceItemName(this.label) : this.label;
  }

  /** Test the predicate against roll options plus this item's own `parent:` options. */
  test(state: EngineState, rollOptions?: Set<string>): boolean {
    if (this.ignored) return false;
    if (this.predicate.length === 0) return true;
    const options = new Set([...(rollOptions ?? state.rollOptions.get()), ...this.item.getRollOptions("parent")]);
    return testPredicate(this.predicate, options);
  }
}

/** Foundry's `reduceItemName`: strip an "Effect: " / "Stance: " style prefix. */
export function reduceItemName(name: string): string {
  return name.replace(/^(?:[A-Z][a-z]+ ?)+: /, "");
}
